import { resolveRuntimeContext } from './context.js';
import { countMetric } from './metrics.js';

const FLOW_STORAGE_KEY = 'h5_stability_core_flow_v1';
const FLOW_EXPIRE_MS = 24 * 60 * 60 * 1000;
const REQUIRED_COMPLETE_STEPS = ['home', 'add_photo', 'photo_selected'];

/**
 * 获取当前时间戳。
 * @returns {number} 当前时间戳。
 */
function getNow() {
  return Date.now();
}

/**
 * 创建本地流程 ID。
 * @returns {string} 本地流程 ID。
 */
function createFlowId() {
  return `${getNow()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 读取流程状态。
 * @returns {Object|null} 流程状态。
 */
function readFlowState() {
  try {
    // localStorage 不可用时返回空状态，不影响业务流程。
    if (!window.localStorage) {
      return null;
    }
    const value = window.localStorage.getItem(FLOW_STORAGE_KEY);
    // 本地尚无流程时返回空状态。
    if (!value) {
      return null;
    }
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

/**
 * 写入流程状态。
 * @param {Object} state - 流程状态。
 * @returns {void}
 */
function writeFlowState(state) {
  try {
    // localStorage 不可用时跳过写入，不影响业务流程。
    if (!window.localStorage) {
      return;
    }
    window.localStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {}
}

/**
 * 清理流程状态。
 * @returns {void}
 */
function clearFlowState() {
  try {
    // localStorage 不可用时跳过清理。
    if (!window.localStorage) {
      return;
    }
    window.localStorage.removeItem(FLOW_STORAGE_KEY);
  } catch (error) {}
}

/**
 * 判断流程是否过期。
 * @param {Object} state - 流程状态。
 * @param {number} now - 当前时间戳。
 * @returns {boolean} 是否过期。
 */
function isExpired(state, now) {
  return Boolean(state && now - Number(state.startedAt || 0) > FLOW_EXPIRE_MS);
}

/**
 * 处理过期流程。
 * @param {Object|null} state - 流程状态。
 * @param {number} now - 当前时间戳。
 * @returns {Object|null} 未过期流程状态。
 */
function expireFlowIfNeeded(state, now) {
  // 不存在流程状态时无需处理。
  if (!state) {
    return null;
  }
  // 未过期流程继续复用。
  if (!isExpired(state, now)) {
    return state;
  }
  countMetric('h5.core_flow.termination', {
    runtime_scene: state.runtimeScene,
    status: 'timeout'
  });
  clearFlowState();
  return null;
}

/**
 * 创建新的核心流程状态。
 * @param {number} now - 当前时间戳。
 * @returns {Object} 新流程状态。
 */
function createFlowState(now) {
  const context = resolveRuntimeContext();
  return {
    flowId: createFlowId(),
    runtimeScene: context.runtimeScene,
    startedAt: now,
    steps: {
      home: now
    },
    status: 'in_progress',
    endedAt: null
  };
}

/**
 * 开始或恢复核心流程。
 * @returns {Object} 当前流程状态。
 */
export function beginOrResumeCoreFlow() {
  const now = getNow();
  const activeState = expireFlowIfNeeded(readFlowState(), now);
  // 存在进行中的流程时复用本地流程状态。
  if (activeState && activeState.status === 'in_progress') {
    return activeState;
  }
  const state = createFlowState(now);
  writeFlowState(state);
  return state;
}

/**
 * 标记核心流程阶段。
 * @param {string} step - 阶段名。
 * @returns {void}
 */
export function markCoreFlowStep(step) {
  const now = getNow();
  const state = expireFlowIfNeeded(readFlowState(), now);
  // 非进行中流程不记录阶段。
  if (!state || state.status !== 'in_progress') {
    return;
  }
  state.steps = {
    ...(state.steps || {}),
    [step]: state.steps && state.steps[step] ? state.steps[step] : now
  };
  writeFlowState(state);
}

/**
 * 判断流程是否已满足完成条件。
 * @param {Object} state - 流程状态。
 * @returns {boolean} 是否满足完成条件。
 */
function hasRequiredCompleteSteps(state) {
  const steps = (state && state.steps) || {};
  return REQUIRED_COMPLETE_STEPS.every((step) => Boolean(steps[step]));
}

/**
 * 完成核心流程。
 * @returns {boolean} 是否记录了完成。
 */
export function completeCoreFlow() {
  const now = getNow();
  const state = expireFlowIfNeeded(readFlowState(), now);
  // 未满足核心路径阶段时不记录完成。
  if (
    !state ||
    state.status !== 'in_progress' ||
    !hasRequiredCompleteSteps(state)
  ) {
    return false;
  }
  state.status = 'completed';
  state.endedAt = now;
  writeFlowState(state);
  countMetric('h5.core_flow.termination', {
    runtime_scene: state.runtimeScene,
    status: 'completed'
  });
  clearFlowState();
  return true;
}

/**
 * 获取当前核心流程状态。
 * @returns {Object|null} 当前流程状态。
 */
export function getCoreFlowState() {
  return expireFlowIfNeeded(readFlowState(), getNow());
}
